import { useState, useEffect, useRef } from "react";
import { useAuth, API, authFetch } from "@/App";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Zap, Mail, History, Shield, LogOut, 
  Plus, Trash2, Edit2, Save, X, Loader2, BookOpen, Users, Upload, FileSpreadsheet, Table, ScrollText, Building2, Settings2, UserPlus, UserX, Lock, Key, Regex, Palette
} from "lucide-react";
import MappingEditor from "@/components/MappingEditor";
import { ExtractionRulesManager } from "@/components/admin/ExtractionRulesManager";
import { AdminAnalytics } from "@/components/admin/AdminAnalytics";
import { ColorLookupManager } from "@/components/admin/ColorLookupManager";
import { FabricSwatchLibrary } from "@/components/fabric/FabricSwatchLibrary";

// Sidebar Component for Admin
const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { icon: FileSpreadsheet, label: "Tableau", path: "/tableau" },
    { icon: Mail, label: "Extracteur", path: "/dashboard" },
    { icon: History, label: "Historique", path: "/history" },
    { icon: Shield, label: "Admin", path: "/admin" },
  ];

  return (
    <aside className="sidebar flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-zinc-950 font-['Manrope']">OrderFlow</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                  location.pathname === item.path
                    ? "bg-orange-50 text-orange-600"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 mb-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 bg-zinc-200 rounded-full flex items-center justify-center">
              <span className="text-zinc-600 font-medium">
                {user?.name?.charAt(0) || "U"}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};

// Company Profile Editor Modal
const ProfileEditorModal = ({ profile, isOpen, onClose, onSave, isNew = false, isSaving }) => {
  const [name, setName] = useState(profile?.name || "");
  const [mappingRules, setMappingRules] = useState(profile?.mapping_rules || []);
  const [detectionKeywords, setDetectionKeywords] = useState(
    profile?.detection_keywords?.join(", ") || ""
  );
  const [exampleEmail, setExampleEmail] = useState(profile?.example_email || "");
  const [isActive, setIsActive] = useState(profile?.is_active ?? true);

  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      // Handle both old (string) and new (array) format
      setMappingRules(Array.isArray(profile.mapping_rules) ? profile.mapping_rules : []);
      setDetectionKeywords(profile.detection_keywords?.join(", ") || "");
      setExampleEmail(profile.example_email || "");
      setIsActive(profile.is_active ?? true);
    } else {
      setName("");
      setMappingRules([]);
      setDetectionKeywords("");
      setExampleEmail("");
      setIsActive(true);
    }
  }, [profile, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Le nom est requis");
      return;
    }

    const keywords = detectionKeywords
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    await onSave({
      name: name.trim(),
      mapping_rules: mappingRules,
      detection_keywords: keywords,
      example_email: exampleEmail,
      is_active: isActive,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <Building2 className="w-5 h-5 text-orange-500" />
            {isNew ? "Nouveau profil entreprise" : "Modifier le profil"}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? "Créez un profil avec des règles de mapping spécifiques pour cette entreprise."
              : "Modifiez les règles de mapping pour cette entreprise."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Name & Keywords Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profileName">Nom de l'entreprise</Label>
                <Input
                  id="profileName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Stores & Pergolas®"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="detectionKeywords">Mots-clés de détection</Label>
                <Input
                  id="detectionKeywords"
                  value={detectionKeywords}
                  onChange={(e) => setDetectionKeywords(e.target.value)}
                  placeholder="Ex: STORES PERGOLAS, SP®"
                  data-testid="profile-keywords-input"
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
              <div>
                <Label>Profil actif</Label>
                <p className="text-sm text-zinc-500">
                  Les profils actifs sont disponibles lors de l'extraction
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
                data-testid="profile-active-switch"
              />
            </div>

            {/* Mapping Editor */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-orange-500" />
                <Label className="text-base font-semibold">Règles de mapping</Label>
              </div>
              <MappingEditor
                rules={mappingRules}
                onRulesChange={setMappingRules}
                exampleEmail={exampleEmail}
                onExampleEmailChange={setExampleEmail}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            data-testid="save-profile-btn"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Example Editor Modal
const ExampleEditorModal = ({ example, isOpen, onClose, onSave, isNew = false }) => {
  const [name, setName] = useState(example?.name || "");
  const [rawEmail, setRawEmail] = useState(example?.raw_email || "");
  const [expectedExtraction, setExpectedExtraction] = useState(
    example?.expected_extraction ? JSON.stringify(example.expected_extraction, null, 2) : ""
  );
  const [isActive, setIsActive] = useState(example?.is_active ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState("");

  useEffect(() => {
    if (example) {
      setName(example.name || "");
      setRawEmail(example.raw_email || "");
      setExpectedExtraction(
        example.expected_extraction ? JSON.stringify(example.expected_extraction, null, 2) : ""
      );
      setIsActive(example.is_active ?? true);
    } else {
      setName("");
      setRawEmail("");
      setExpectedExtraction("");
      setIsActive(true);
    }
    setJsonError("");
  }, [example, isOpen]);

  const validateJson = (text) => {
    try {
      JSON.parse(text);
      setJsonError("");
      return true;
    } catch (e) {
      setJsonError("JSON invalide: " + e.message);
      return false;
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Le nom est requis");
      return;
    }
    if (!rawEmail.trim()) {
      toast.error("L'email exemple est requis");
      return;
    }
    if (!expectedExtraction.trim()) {
      toast.error("L'extraction attendue est requise");
      return;
    }
    if (!validateJson(expectedExtraction)) {
      toast.error("Le JSON de l'extraction est invalide");
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        raw_email: rawEmail.trim(),
        expected_extraction: JSON.parse(expectedExtraction),
        is_active: isActive,
      });
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <BookOpen className="w-5 h-5 text-orange-500" />
            {isNew ? "Nouvel exemple" : "Modifier l'exemple"}
          </DialogTitle>
          <DialogDescription>
            {isNew 
              ? "Créez un exemple d'email avec l'extraction attendue pour améliorer la précision de l'IA."
              : "Modifiez cet exemple pour ajuster l'apprentissage de l'IA."
            }
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Nom de l'exemple</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Commande fournisseur ABC"
                data-testid="example-name-input"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Exemple actif</Label>
                <p className="text-sm text-zinc-500">
                  Les exemples actifs sont utilisés par l'IA lors des extractions
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={setIsActive}
                data-testid="example-active-switch"
              />
            </div>

            {/* Raw Email */}
            <div className="space-y-2">
              <Label htmlFor="rawEmail">Email exemple (contenu brut)</Label>
              <Textarea
                id="rawEmail"
                value={rawEmail}
                onChange={(e) => setRawEmail(e.target.value)}
                placeholder="Collez ici un exemple d'email de commande..."
                className="min-h-[200px] font-mono text-sm"
                data-testid="example-email-input"
              />
            </div>

            {/* Expected Extraction */}
            <div className="space-y-2">
              <Label htmlFor="expectedExtraction">
                Extraction attendue (JSON)
                {jsonError && (
                  <span className="ml-2 text-red-500 text-xs">{jsonError}</span>
                )}
              </Label>
              <Textarea
                id="expectedExtraction"
                value={expectedExtraction}
                onChange={(e) => {
                  setExpectedExtraction(e.target.value);
                  if (e.target.value.trim()) {
                    validateJson(e.target.value);
                  } else {
                    setJsonError("");
                  }
                }}
                placeholder={`{
  "client_name": "Nom du client",
  "client_email": "email@example.com",
  "products": [
    {"name": "Produit", "quantity": "10", "unit_price": "25€", "total": "250€"}
  ],
  "total_amount": "250€"
}`}
                className={`min-h-[250px] font-mono text-sm ${jsonError ? "border-red-500" : ""}`}
                data-testid="example-extraction-input"
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 border-t border-zinc-200">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            data-testid="save-example-btn"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [examples, setExamples] = useState([]);
  const [users, setUsers] = useState([]);
  const [columnTemplate, setColumnTemplate] = useState(null);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [originalInstructions, setOriginalInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isNewExample, setIsNewExample] = useState(false);
  
  // Company profile editor state
  const [isProfileEditorOpen, setIsProfileEditorOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState("");
  const [isAddingToWhitelist, setIsAddingToWhitelist] = useState(false);

  // Create user with password state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: "", password: "", name: "" });
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("config");
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Accès refusé - Admin requis");
      navigate("/tableau");
    }
  }, [user, navigate]);

  // Fetch all admin data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [examplesRes, usersRes, columnsRes, instructionsRes, profilesRes, whitelistRes] = await Promise.all([
          authFetch(`${API}/admin/examples`, {}),
          authFetch(`${API}/admin/users`, {}),
          authFetch(`${API}/admin/columns`, {}),
          authFetch(`${API}/admin/instructions`, {}),
          authFetch(`${API}/admin/company-profiles`, {}),
          authFetch(`${API}/admin/whitelist`, {})
        ]);

        if (examplesRes.ok) {
          const data = await examplesRes.json();
          setExamples(data.examples || []);
        }

        if (usersRes.ok) {
          const data = await usersRes.json();
          setUsers(data.users || []);
        }

        if (columnsRes.ok) {
          const data = await columnsRes.json();
          setColumnTemplate(data);
        }

        if (instructionsRes.ok) {
          const data = await instructionsRes.json();
          setInstructions(data.content || "");
          setOriginalInstructions(data.content || "");
        }

        if (profilesRes.ok) {
          const data = await profilesRes.json();
          setCompanyProfiles(data.profiles || []);
        }

        if (whitelistRes.ok) {
          const data = await whitelistRes.json();
          setWhitelist(data.emails || []);
        }
      } catch (error) {
        console.error("Failed to fetch admin data:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  // Save instructions
  const handleSaveInstructions = async () => {
    setIsSavingInstructions(true);
    try {
      const response = await authFetch(`${API}/admin/instructions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: instructions }),
      });

      if (response.ok) {
        toast.success("Instructions enregistrées");
        setOriginalInstructions(instructions);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSavingInstructions(false);
    }
  };

  // Save company profile
  const handleSaveProfile = async (profileData) => {
    setIsSavingProfile(true);
    try {
      const url = isNewProfile 
        ? `${API}/admin/company-profiles`
        : `${API}/admin/company-profiles/${selectedProfile.profile_id}`;
      
      const response = await fetch(url, {
        method: isNewProfile ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        toast.success(isNewProfile ? "Profil créé" : "Profil mis à jour");
        // Refresh profiles
        const profilesRes = await authFetch(`${API}/admin/company-profiles`, {});
        if (profilesRes.ok) {
          const data = await profilesRes.json();
          setCompanyProfiles(data.profiles || []);
        }
        setIsProfileEditorOpen(false);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Delete company profile
  const handleDeleteProfile = async (profileId) => {
    if (!confirm("Supprimer ce profil ?")) return;

    try {
      const response = await authFetch(`${API}/admin/company-profiles/${profileId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Profil supprimé");
        setCompanyProfiles(companyProfiles.filter(p => p.profile_id !== profileId));
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Upload column template
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Veuillez sélectionner un fichier Excel (.xlsx)");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await authFetch(`${API}/admin/columns/upload`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Template uploadé: ${data.columns_count} colonnes détectées`);
        // Refresh column template
        const columnsRes = await authFetch(`${API}/admin/columns`, {});
        if (columnsRes.ok) {
          setColumnTemplate(await columnsRes.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'upload");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete column template
  const handleDeleteTemplate = async () => {
    if (!confirm("Supprimer le template de colonnes ?")) return;

    try {
      const response = await authFetch(`${API}/admin/columns`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Template supprimé");
        setColumnTemplate({ configured: false, columns: [] });
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Create example
  const handleCreateExample = async (data) => {
    try {
      const response = await authFetch(`${API}/admin/examples`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success("Exemple créé avec succès");
        // Refresh examples
        const examplesRes = await authFetch(`${API}/admin/examples`, {});
        if (examplesRes.ok) {
          const exData = await examplesRes.json();
          setExamples(exData.examples || []);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
        throw new Error(error.detail);
      }
    } catch (error) {
      throw error;
    }
  };

  // Update example
  const handleUpdateExample = async (data) => {
    if (!selectedExample) return;
    
    try {
      const response = await authFetch(`${API}/admin/examples/${selectedExample.example_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success("Exemple mis à jour");
        // Refresh examples
        const examplesRes = await authFetch(`${API}/admin/examples`, {});
        if (examplesRes.ok) {
          const exData = await examplesRes.json();
          setExamples(exData.examples || []);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
        throw new Error(error.detail);
      }
    } catch (error) {
      throw error;
    }
  };

  // Delete example
  const handleDeleteExample = async (exampleId) => {
    if (!confirm("Supprimer cet exemple ?")) return;

    try {
      const response = await authFetch(`${API}/admin/examples/${exampleId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Exemple supprimé");
        setExamples(examples.filter(e => e.example_id !== exampleId));
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Make user admin
  const handleMakeAdmin = async (userId) => {
    if (!confirm("Donner les droits admin à cet utilisateur ?")) return;

    try {
      const response = await authFetch(`${API}/admin/users/${userId}/make-admin`, {
        method: "POST",
      });

      if (response.ok) {
        toast.success("Utilisateur promu admin");
        setUsers(users.map(u => 
          u.user_id === userId ? { ...u, is_admin: true } : u
        ));
      } else {
        toast.error("Erreur lors de la promotion");
      }
    } catch (error) {
      toast.error("Erreur lors de la promotion");
    }
  };

  // Add email to whitelist
  const handleAddToWhitelist = async () => {
    if (!newWhitelistEmail.trim() || !newWhitelistEmail.includes("@")) {
      toast.error("Email invalide");
      return;
    }

    setIsAddingToWhitelist(true);
    try {
      const response = await authFetch(`${API}/admin/whitelist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newWhitelistEmail.trim().toLowerCase() }),
      });

      if (response.ok) {
        const data = await response.json();
        setWhitelist(data.emails || []);
        setNewWhitelistEmail("");
        toast.success("Email ajouté à la liste blanche");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'ajout");
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setIsAddingToWhitelist(false);
    }
  };

  // Remove email from whitelist
  const handleRemoveFromWhitelist = async (email) => {
    if (!confirm(`Retirer ${email} de la liste blanche ? Cet utilisateur ne pourra plus se connecter.`)) return;

    try {
      const response = await authFetch(`${API}/admin/whitelist/${encodeURIComponent(email)}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const data = await response.json();
        setWhitelist(data.emails || []);
        toast.success("Email retiré de la liste blanche");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Create user with password
  const handleCreateUser = async () => {
    if (!newUserForm.email || !newUserForm.password || !newUserForm.name) {
      toast.error("Tous les champs sont requis");
      return;
    }
    if (newUserForm.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setIsCreatingUser(true);
    try {
      const response = await authFetch(`${API}/admin/users/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newUserForm),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Utilisateur ${data.email} créé avec succès`);
        setShowCreateUserModal(false);
        setNewUserForm({ email: "", password: "", name: "" });
        
        // Refresh users and whitelist
        const [usersRes, whitelistRes] = await Promise.all([
          authFetch(`${API}/admin/users`, {}),
          authFetch(`${API}/admin/whitelist`, {})
        ]);
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.users || []);
        }
        if (whitelistRes.ok) {
          const whitelistData = await whitelistRes.json();
          setWhitelist(whitelistData.emails || []);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Supprimer l'utilisateur ${userName} ? Cette action est irréversible.`)) return;

    try {
      const response = await authFetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Utilisateur supprimé");
        setUsers(users.filter(u => u.user_id !== userId));
        // Refresh whitelist
        const whitelistRes = await authFetch(`${API}/admin/whitelist`, {});
        if (whitelistRes.ok) {
          const data = await whitelistRes.json();
          setWhitelist(data.emails || []);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar />
      
      <main className="main-content flex-1" data-testid="admin-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 font-['Manrope']">
            Administration
          </h1>
          <p className="text-zinc-600 mt-1">
            Gérez les exemples d'extraction et les utilisateurs
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 bg-white border border-zinc-200 rounded-lg p-1 w-fit" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab("config")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === "config" ? "bg-orange-500 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid="tab-config"
          >
            <span className="flex items-center gap-2"><Settings2 className="w-4 h-4" />Configuration</span>
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === "analytics" ? "bg-orange-500 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            data-testid="tab-analytics"
          >
            <span className="flex items-center gap-2"><Table className="w-4 h-4" />Analytics</span>
          </button>
        </div>

        {activeTab === "analytics" ? (
          <AdminAnalytics />
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" style={{ width: 40, height: 40 }}></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Column Template Section */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Table className="w-6 h-6 text-orange-500" />
                  <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">
                    Colonnes attendues
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".xlsx,.xls"
                    className="hidden"
                    data-testid="column-file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                    data-testid="upload-columns-btn"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Upload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Importer Excel
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-sm text-zinc-500 mb-4">
                Uploadez un fichier Excel avec les en-têtes de colonnes en première ligne. 
                L'IA utilisera ces colonnes pour structurer l'extraction.
              </p>

              {columnTemplate?.configured ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">{columnTemplate.filename}</p>
                        <p className="text-sm text-emerald-600">
                          {columnTemplate.columns?.length} colonnes configurées
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteTemplate}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Display columns */}
                  <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
                      Colonnes détectées
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {columnTemplate.columns?.map((col, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-1.5 bg-white border border-zinc-200 rounded-md text-sm"
                        >
                          <span className="font-medium text-zinc-800">{col.name}</span>
                          {col.sample_values?.length > 0 && (
                            <span className="text-zinc-400 ml-2 text-xs">
                              ex: {col.sample_values[0]}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-lg">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="text-zinc-500">Aucun template de colonnes configuré</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Cliquez sur "Importer Excel" pour définir les colonnes attendues
                  </p>
                </div>
              )}
            </div>

            {/* Unified Extraction Rules Section */}
            <ExtractionRulesManager />

            {/* Color Lookup Table Section */}
            <ColorLookupManager />

            {/* Fabric Swatch Library Section */}
            <FabricSwatchLibrary />

            {/* Company Profiles Section */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="company-profiles-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-orange-500" />
                  <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">
                    Profils d'entreprise
                  </h2>
                  <span className="text-sm text-zinc-500">
                    ({companyProfiles.length} profil{companyProfiles.length > 1 ? "s" : ""})
                  </span>
                </div>
                <Button
                  onClick={() => {
                    setSelectedProfile(null);
                    setIsNewProfile(true);
                    setIsProfileEditorOpen(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                  data-testid="add-profile-btn"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un profil
                </Button>
              </div>

              <p className="text-sm text-zinc-500 mb-4">
                Créez des profils pour chaque fournisseur/entreprise avec des règles de mapping spécifiques. 
                L'IA détectera automatiquement le profil ou vous pourrez le sélectionner manuellement.
              </p>

              {companyProfiles.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                  <p>Aucun profil d'entreprise configuré</p>
                  <p className="text-sm mt-1">
                    Ajoutez des profils pour gérer différents formats d'emails
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {companyProfiles.map((profile) => {
                    const rulesCount = Array.isArray(profile.mapping_rules) ? profile.mapping_rules.length : 0;
                    return (
                      <div
                        key={profile.profile_id}
                        className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg border border-zinc-200"
                        data-testid={`profile-item-${profile.profile_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-3 h-3 rounded-full ${profile.is_active ? "bg-emerald-500" : "bg-zinc-300"}`} />
                          <div>
                            <p className="font-medium text-zinc-900">{profile.name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              {rulesCount > 0 && (
                                <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                  {rulesCount} règle{rulesCount > 1 ? "s" : ""} de mapping
                                </span>
                              )}
                              {profile.detection_keywords?.length > 0 && (
                                <span className="text-xs text-zinc-500">
                                  Mots-clés: {profile.detection_keywords.slice(0, 2).join(", ")}
                                  {profile.detection_keywords.length > 2 && ` +${profile.detection_keywords.length - 2}`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!profile.is_active && (
                            <span className="text-xs text-zinc-400 mr-2">(inactif)</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedProfile(profile);
                              setIsNewProfile(false);
                              setIsProfileEditorOpen(true);
                            }}
                            className="gap-1"
                            data-testid={`edit-profile-${profile.profile_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                            Modifier
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProfile(profile.profile_id)}
                            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-profile-${profile.profile_id}`}
                          >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Examples Section */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-6 h-6 text-orange-500" />
                  <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">
                    Exemples d'extraction
                  </h2>
                  <span className="text-sm text-zinc-500">
                    ({examples.length} exemple{examples.length > 1 ? "s" : ""})
                  </span>
                </div>
                <Button
                  onClick={() => {
                    setSelectedExample(null);
                    setIsNewExample(true);
                    setIsEditorOpen(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                  data-testid="add-example-btn"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un exemple
                </Button>
              </div>

              {examples.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                  <p>Aucun exemple configuré</p>
                  <p className="text-sm mt-1">
                    Ajoutez des exemples pour améliorer la précision de l'IA
                  </p>
                </div>
              ) : (
                <Accordion type="single" collapsible className="space-y-2">
                  {examples.map((example) => (
                    <AccordionItem
                      key={example.example_id}
                      value={example.example_id}
                      className="border border-zinc-200 rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-2 h-2 rounded-full ${example.is_active ? "bg-emerald-500" : "bg-zinc-300"}`} />
                          <span className="font-medium">{example.name}</span>
                          {!example.is_active && (
                            <span className="text-xs text-zinc-400">(inactif)</span>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-2 pb-4">
                          <div>
                            <Label className="text-xs text-zinc-500 uppercase tracking-wider">Email exemple</Label>
                            <pre className="mt-1 p-3 bg-zinc-50 rounded-md text-sm font-mono whitespace-pre-wrap max-h-40 overflow-auto">
                              {example.raw_email}
                            </pre>
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500 uppercase tracking-wider">Extraction attendue</Label>
                            <pre className="mt-1 p-3 bg-zinc-50 rounded-md text-sm font-mono whitespace-pre-wrap max-h-40 overflow-auto">
                              {JSON.stringify(example.expected_extraction, null, 2)}
                            </pre>
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedExample(example);
                                setIsNewExample(false);
                                setIsEditorOpen(true);
                              }}
                              className="gap-1"
                            >
                              <Edit2 className="w-4 h-4" />
                              Modifier
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteExample(example.example_id)}
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>

            {/* Whitelist Section */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="whitelist-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <UserPlus className="w-6 h-6 text-orange-500" />
                  <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">
                    Utilisateurs autorisés
                  </h2>
                  <span className="text-sm text-zinc-500">
                    ({whitelist.length} email{whitelist.length > 1 ? "s" : ""})
                  </span>
                </div>
                <Button
                  onClick={() => setShowCreateUserModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                  data-testid="create-user-btn"
                >
                  <Key className="w-4 h-4" />
                  Créer un compte
                </Button>
              </div>

              <p className="text-sm text-zinc-500 mb-4">
                Seuls les emails listés ci-dessous peuvent se connecter à l'application. 
                Votre email administrateur (<strong>{user?.email}</strong>) a toujours accès.
              </p>

              {/* Add email to whitelist (for Google login) */}
              <div className="p-4 bg-zinc-50 rounded-lg mb-4">
                <p className="text-sm font-medium text-zinc-700 mb-2">Autoriser un email (connexion Google)</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={newWhitelistEmail}
                    onChange={(e) => setNewWhitelistEmail(e.target.value)}
                    placeholder="email@exemple.com"
                    className="flex-1"
                    onKeyDown={(e) => e.key === "Enter" && handleAddToWhitelist()}
                    data-testid="whitelist-email-input"
                  />
                  <Button
                    onClick={handleAddToWhitelist}
                    disabled={isAddingToWhitelist || !newWhitelistEmail.trim()}
                    variant="outline"
                    className="gap-2"
                    data-testid="add-whitelist-btn"
                  >
                    {isAddingToWhitelist ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Ajouter
                  </Button>
                </div>
              </div>

              {/* Whitelist emails */}
              {whitelist.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-lg">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="text-zinc-500">Aucun utilisateur autorisé</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Ajoutez des emails pour permettre à d'autres utilisateurs de se connecter
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {whitelist.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                      data-testid={`whitelist-item-${email}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-600 text-sm font-medium">
                            {email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-zinc-900">{email}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveFromWhitelist(email)}
                        className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-whitelist-${email}`}
                      >
                        <UserX className="w-4 h-4" />
                        Retirer
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Users Section */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-orange-500" />
                <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">
                  Utilisateurs connectés
                </h2>
                <span className="text-sm text-zinc-500">
                  ({users.length} utilisateur{users.length > 1 ? "s" : ""})
                </span>
              </div>

              <p className="text-sm text-zinc-500 mb-4">
                Liste des utilisateurs qui se sont déjà connectés à l'application.
              </p>

              {users.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p>Aucun utilisateur connecté</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                            <span className="text-zinc-600 text-sm font-medium">
                              {u.name?.charAt(0) || "U"}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-zinc-900">{u.name}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.auth_type === "password" && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Email/MDP
                          </span>
                        )}
                        {u.is_admin ? (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            Admin
                          </span>
                        ) : (
                          <>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              Utilisateur
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.user_id, u.name)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                              title="Supprimer l'utilisateur"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Example Editor Modal */}
        <ExampleEditorModal
          example={isNewExample ? null : selectedExample}
          isOpen={isEditorOpen}
          onClose={() => {
            setIsEditorOpen(false);
            setSelectedExample(null);
          }}
          onSave={isNewExample ? handleCreateExample : handleUpdateExample}
          isNew={isNewExample}
        />

        {/* Profile Editor Modal */}
        <ProfileEditorModal
          profile={isNewProfile ? null : selectedProfile}
          isOpen={isProfileEditorOpen}
          onClose={() => {
            setIsProfileEditorOpen(false);
            setSelectedProfile(null);
          }}
          onSave={handleSaveProfile}
          isNew={isNewProfile}
          isSaving={isSavingProfile}
        />

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
              <div className="p-6 border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-orange-500" />
                    <h3 className="text-lg font-semibold text-zinc-900">Créer un compte utilisateur</h3>
                  </div>
                  <button 
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setNewUserForm({ email: "", password: "", name: "" });
                    }}
                    className="text-zinc-400 hover:text-zinc-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-sm text-zinc-500">
                  Créez un compte avec email et mot de passe. L'utilisateur pourra se connecter sans Google.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Nom complet</label>
                  <Input
                    type="text"
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Jean Dupont"
                    data-testid="new-user-name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="utilisateur@exemple.com"
                    data-testid="new-user-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Mot de passe</label>
                  <Input
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Minimum 6 caractères"
                    data-testid="new-user-password"
                  />
                </div>
              </div>
              
              <div className="p-6 border-t border-zinc-200 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setNewUserForm({ email: "", password: "", name: "" });
                  }}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCreateUser}
                  disabled={isCreatingUser || !newUserForm.email || !newUserForm.password || !newUserForm.name}
                  className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                  data-testid="confirm-create-user-btn"
                >
                  {isCreatingUser ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Création...</>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Créer le compte
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
